import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { CheckCircle2, Calendar, ArrowRight, Shield, Zap, Clock, Star, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

export default function ThankYou() {
  const [booked, setBooked] = useState(false);

  return (
    <div className="relative pt-32 pb-24 overflow-hidden min-h-screen flex flex-col items-center">
      {/* Background Elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,150,255,0.05),transparent_50%)]" />
        <div className="absolute top-0 left-0 w-full h-full cyber-grid opacity-10" />
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10 max-w-5xl">
        <div className="text-center space-y-8 mb-20">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-24 h-24 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center text-accent mx-auto mb-8 shadow-[0_0_50px_rgba(0,150,255,0.2)]"
          >
            <CheckCircle2 className="w-12 h-12" />
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-7xl font-display font-bold tracking-tighter"
          >
            Intelligence <span className="text-accent">Received</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-muted-foreground font-light max-w-2xl mx-auto"
          >
            Your security profile has been successfully transmitted to our senior intelligence team. We are currently analyzing your data.
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Step 2: Book a Call (Conversion Optimization) */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card p-10 rounded-[3rem] border-white/10 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-6">
              <div className="px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent text-[8px] font-bold uppercase tracking-widest animate-pulse">
                Recommended Next Step
              </div>
            </div>
            
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl font-display font-bold tracking-tight">Skip the Queue</h2>
                <p className="text-muted-foreground font-light leading-relaxed">
                  Don't wait for our call. Book a 15-minute discovery session directly with a senior security architect to discuss your results in real-time.
                </p>
              </div>

              <AnimatePresence mode="wait">
                {!booked ? (
                  <motion.div 
                    key="booking"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-6"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 rounded-2xl bg-white/5 border border-white/10 text-center cursor-pointer hover:bg-accent/10 hover:border-accent/30 transition-all group">
                        <p className="text-[10px] font-bold text-muted-foreground uppercase mb-1">Tomorrow</p>
                        <p className="text-lg font-bold">10:00 AM</p>
                      </div>
                      <div className="p-4 rounded-2xl bg-white/5 border border-white/10 text-center cursor-pointer hover:bg-accent/10 hover:border-accent/30 transition-all group">
                        <p className="text-[10px] font-bold text-muted-foreground uppercase mb-1">Tomorrow</p>
                        <p className="text-lg font-bold">2:30 PM</p>
                      </div>
                      <div className="p-4 rounded-2xl bg-white/5 border border-white/10 text-center cursor-pointer hover:bg-accent/10 hover:border-accent/30 transition-all group">
                        <p className="text-[10px] font-bold text-muted-foreground uppercase mb-1">Thursday</p>
                        <p className="text-lg font-bold">9:00 AM</p>
                      </div>
                      <div className="p-4 rounded-2xl bg-white/5 border border-white/10 text-center cursor-pointer hover:bg-accent/10 hover:border-accent/30 transition-all group">
                        <p className="text-[10px] font-bold text-muted-foreground uppercase mb-1">Thursday</p>
                        <p className="text-lg font-bold">4:00 PM</p>
                      </div>
                    </div>
                    <Button 
                      onClick={() => setBooked(true)}
                      className="w-full h-16 rounded-2xl bg-accent hover:bg-accent/90 text-background font-bold shadow-[0_0_30px_rgba(0,150,255,0.3)]"
                    >
                      <Calendar className="w-5 h-5 mr-2" /> Confirm Booking
                    </Button>
                  </motion.div>
                ) : (
                  <motion.div 
                    key="confirmed"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="p-8 rounded-2xl bg-green-500/10 border border-green-500/20 text-center space-y-4"
                  >
                    <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" />
                    <h3 className="text-2xl font-display font-bold text-green-500">Call Confirmed!</h3>
                    <p className="text-sm text-muted-foreground">Check your email for the calendar invite and meeting link.</p>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10">
                <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center text-accent">
                  <Clock className="w-5 h-5" />
                </div>
                <p className="text-xs text-muted-foreground font-light italic">
                  "The discovery call was the most valuable 15 minutes I've spent on security this year." — CTO, FinTech Corp
                </p>
              </div>
            </div>
          </motion.div>

          {/* Step 3: Upsell Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-8"
          >
            <div className="p-10 rounded-[3rem] bg-gradient-to-br from-accent/20 to-transparent border border-accent/30 space-y-8 relative overflow-hidden group">
              <div className="absolute -top-24 -right-24 w-64 h-64 bg-accent/20 blur-[80px] rounded-full group-hover:bg-accent/30 transition-all duration-700" />
              
              <div className="space-y-4 relative z-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 border border-accent/30 text-accent text-[9px] font-bold uppercase tracking-widest">
                  Exclusive Offer
                </div>
                <h2 className="text-4xl font-display font-bold tracking-tight">Priority <span className="text-accent">Deep-Scan</span></h2>
                <p className="text-muted-foreground font-light leading-relaxed">
                  While your free audit is being processed, you can upgrade to a **Priority Deep-Scan** for immediate, exhaustive penetration testing.
                </p>
              </div>

              <div className="space-y-4 relative z-10">
                <div className="flex items-center gap-3">
                  <Star className="w-5 h-5 text-accent fill-accent" />
                  <span className="text-sm font-medium">Full Internal Network Penetration Test</span>
                </div>
                <div className="flex items-center gap-3">
                  <Star className="w-5 h-5 text-accent fill-accent" />
                  <span className="text-sm font-medium">Social Engineering Vulnerability Check</span>
                </div>
                <div className="flex items-center gap-3">
                  <Star className="w-5 h-5 text-accent fill-accent" />
                  <span className="text-sm font-medium">24-Hour Emergency Response Guarantee</span>
                </div>
              </div>

              <div className="pt-4 relative z-10">
                <div className="flex items-end gap-2 mb-6">
                  <span className="text-4xl font-display font-bold">$499</span>
                  <span className="text-muted-foreground line-through mb-1">$2,499</span>
                  <span className="text-accent text-xs font-bold mb-1 ml-2">80% OFF — TODAY ONLY</span>
                </div>
                <Button className="w-full h-16 rounded-2xl bg-white text-background hover:bg-white/90 font-bold transition-all hover:scale-[1.02]">
                  Upgrade to Priority Scan <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>

            <div className="flex justify-center">
              <Link to="/" className="text-muted-foreground hover:text-accent text-sm font-light transition-colors flex items-center gap-2">
                <ArrowRight className="w-4 h-4 rotate-180" /> Return to Homepage
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
